/*
  Admin Table Component
  ---------------------
  Reusable table component for admin dashboard tabs.
*/

import React from 'react';

/**
 * Admin table component
 * @param {Object} props
 * @param {Array} props.columns - Column definitions [{ key, label, render?, className? }]
 * @param {Array} props.items - Data items to display
 * @param {Set} props.selectedKeys - Set of selected item keys
 * @param {Function} props.onToggleSelect - Handler for row selection toggle
 * @param {Function} props.onRowClick - Handler for row click
 * @param {Function} props.getRowKey - Function to get unique key from item
 * @param {boolean} props.showDebug - Whether to show debug column
 * @param {string} props.emptyMessage - Message when no items
 */
const AdminTable = ({
  columns,
  items,
  selectedKeys,
  onToggleSelect,
  onRowClick,
  getRowKey,
  showDebug = false,
  emptyMessage = 'No results yet.'
}) => {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {showDebug && (
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Key</th>
            )}
            {columns.map(col => (
              <th 
                key={col.key} 
                className={`px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase ${col.headerClassName || ''}`}
              >
                {col.label}
              </th>
            ))}
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Select</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {items.map((item, idx) => {
            const key = getRowKey(item, idx);
            const isSelected = selectedKeys.has(key);
            
            return (
              <tr 
                key={key} 
                className={`hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''}`}
                onClick={() => onRowClick && onRowClick(item, idx)}
              >
                {showDebug && (
                  <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500 break-all max-w-[280px]">
                    {key}
                  </td>
                )}
                {columns.map(col => (
                  <td 
                    key={col.key} 
                    className={`px-4 py-2 whitespace-nowrap text-sm text-gray-700 ${col.className || ''}`}
                  >
                    {col.render ? col.render(item, idx) : item[col.key]}
                  </td>
                ))}
                <td className="px-4 py-2 whitespace-nowrap text-right text-sm">
                  <input 
                    type="checkbox" 
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      onToggleSelect(key, e.target.checked);
                    }}
                  />
                </td>
              </tr>
            );
          })}
          {items.length === 0 && (
            <tr>
              <td 
                className="px-4 py-6 text-center text-sm text-gray-500" 
                colSpan={columns.length + (showDebug ? 2 : 1)}
              >
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AdminTable;
